import {
  Box,
  Button,
  Card,
  CardHeader,
  Container,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { TOKEN_KEY, url, imgUrl } from 'src/Helpers/utils/Constant';
import { TableLoader, TableNotFound } from 'src/Utils/TableLoader';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import { Edit } from '@mui/icons-material';
import DeleteConfirmation from 'src/Utils/DeleteConfirmation';
import ModalsWrapper from 'src/components/Modals/ModalsWrapper';
import CreateStaff from './component/CreateStaff';
import { IOfficialStaffList } from './types';
import UpdateStaff from './component/UpdateStaff';

function AtabOfficialStaffList() {
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [staffList, setStaffList] = useState<IOfficialStaffList[] | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const token = localStorage.getItem(TOKEN_KEY);

  // ▶ Create Staff Modal state
  const [createOpen, setCreateOpen] = useState(false);
  const handleCreateOpen = () => setCreateOpen(true);
  const handleCreateClose = () => setCreateOpen(false);

  // ▶ Delete Modal state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<IOfficialStaffList | null>(
    null
  );

  // ▶ Edit modal (ready for future)
  const [editOpen, setEditOpen] = useState(false);
  const [editStaff, setEditStaff] = useState<IOfficialStaffList | null>(null);

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        setLoading(true);
        const uri = `${url}/api/v1/admin/office-staff`;
        const { data } = await axios.get(uri, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (data.success) {
          setStaffList(data.data);
        } else {
          setStaffList([]);
        }
      } catch (error) {
        console.error('Failed to fetch staff list', error);
        setStaffList([]);
      } finally {
        setLoading(false);
      }
    };

    fetchStaff();
  }, [refreshTrigger, token]);

  const handleOpenDelete = (staff: IOfficialStaffList) => {
    setSelectedStaff(staff);
    setDeleteOpen(true);
  };

  const handleCloseDelete = () => {
    setDeleteOpen(false);
    setSelectedStaff(null);
  };

  const handleDelete = async () => {
    if (!selectedStaff?.id) return;
    try {
      setDeleteLoading(true);

      await axios.delete(
        `${url}/api/v1/admin/office-staff/${selectedStaff.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setRefreshTrigger((prev) => prev + 1);
      handleCloseDelete();
    } catch (error) {
      console.error('Failed to delete staff', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Container maxWidth='xl'>
      <Card>
        <CardHeader
          action={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <Button
                onClick={handleCreateOpen}
                variant='contained'
                sx={{ ml: 2 }}
              >
                Create Staff
              </Button>
            </Box>
          }
          title='Official Staff List'
        />
        <Divider />

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>SL.</TableCell>
                <TableCell>Staff Name</TableCell>
                <TableCell>Designation</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Image</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>

            {loading ? (
              <TableLoader />
            ) : staffList?.length ? (
              <TableBody>
                {staffList.map((staff: IOfficialStaffList, i: number) => (
                  <TableRow hover key={staff.id || i}>
                    {/* SL */}
                    <TableCell>
                      <Typography
                        variant='body1'
                        color='text.primary'
                        gutterBottom
                        noWrap
                      >
                        {i + 1}
                      </Typography>
                    </TableCell>

                    {/* Name */}
                    <TableCell>
                      <Typography
                        variant='body1'
                        color='text.primary'
                        gutterBottom
                        noWrap
                      >
                        {staff?.name}
                      </Typography>
                    </TableCell>

                    {/* Designation */}
                    <TableCell>
                      <Typography
                        variant='body1'
                        color='text.primary'
                        gutterBottom
                        noWrap
                      >
                        {staff?.designation || 'N/A'}
                      </Typography>
                    </TableCell>

                    {/* Priority */}
                    <TableCell>
                      <Typography
                        variant='body1'
                        color='text.primary'
                        gutterBottom
                        noWrap
                      >
                        {staff?.priority ?? 'N/A'}
                      </Typography>
                    </TableCell>

                    {/* Image */}
                    <TableCell>
                      {staff?.image ? (
                        <Box
                          component='img'
                          src={`${imgUrl}/${staff.image}`}
                          alt={staff.name}
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: 1,
                            objectFit: 'cover',
                            boxShadow: 1,
                          }}
                        />
                      ) : (
                        <Typography variant='body2' color='text.secondary'>
                          No image
                        </Typography>
                      )}
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <Typography
                        variant='body2'
                        color={staff?.status ? 'success.main' : 'error.main'}
                        gutterBottom
                        noWrap
                      >
                        {staff?.status ? 'Active' : 'Inactive'}
                      </Typography>
                    </TableCell>

                    {/* Actions */}
                    <TableCell>
                      {/* EDIT – hook to future EditStaff modal */}
                      <Tooltip title='Edit' arrow>
                        <IconButton
                          sx={{
                            '&:hover': {
                              background: theme.colors.primary.lighter,
                            },
                            color: theme.palette.primary.main,
                          }}
                          color='inherit'
                          size='small'
                          onClick={() => {
                            setEditStaff(staff);
                            setEditOpen(true);
                          }}
                        >
                          <Edit fontSize='small' />
                        </IconButton>
                      </Tooltip>

                      {/* DELETE */}
                      <Tooltip title='Delete' arrow>
                        <IconButton
                          onClick={() => handleOpenDelete(staff)}
                          sx={{
                            '&:hover': {
                              background: theme.colors.error.lighter,
                            },
                            color: theme.palette.error.main,
                          }}
                          color='inherit'
                          size='small'
                        >
                          <DeleteTwoToneIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            ) : (
              <TableNotFound />
            )}
          </Table>
        </TableContainer>

        {/* CREATE STAFF MODAL */}
        <ModalsWrapper
          setShowModal={setCreateOpen}
          showModal={createOpen}
          modalData={{ title: 'Create Staff' }}
          maxWidth='sm'
        >
          <CreateStaff
            handleClose={handleCreateClose}
            setRefreshTrigger={setRefreshTrigger}
          />
        </ModalsWrapper>

        {/* DELETE CONFIRMATION MODAL */}
        <DeleteConfirmation
          open={deleteOpen}
          handleClose={handleCloseDelete}
          text={
            selectedStaff
              ? `Want to delete "${selectedStaff.name}" from official staff list?`
              : 'Want to delete this staff?'
          }
          handleDeleteFun={handleDelete}
          loading={deleteLoading}
        />

        {/* EDIT STAFF MODAL (when you build it) */}
        <ModalsWrapper
          setShowModal={setEditOpen}
          showModal={editOpen}
          modalData={{ title: 'Edit Staff' }}
          maxWidth='sm'
        >
          {editStaff && (
            <UpdateStaff
              handleClose={() => setEditOpen(false)}
              setRefreshTrigger={setRefreshTrigger}
              staff={editStaff}
            />
          )}
        </ModalsWrapper>
      </Card>
    </Container>
  );
}

export default AtabOfficialStaffList;
